import { useAuth } from '@parsleyhealth/front-end-utils';
import { PageButton } from 'components/atoms/Button';
import { LinkButton } from 'components/atoms/Link';
import { Tooltip } from 'components/atoms/Tooltip';
import { AccountIcon } from 'components/icons/AccountIcon';
import { HeartIcon } from 'components/icons/HeartIcon';
import { HomeIcon } from 'components/icons/HomeIcon';
import { LockIcon } from 'components/icons/LockIcon';
import { useCurrentPerson } from 'context/currentPerson';
import { useFirestore } from 'context/firestore';
import React from 'react';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { NavLink, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Events, track } from 'utils/tracking';

import { mailTo } from 'constants/insurance-providers';
import { Colors, Spacing, Device, Zindex } from 'constants/theme';

export const DashboardNavLinks: React.FC = () => {
  const firestore = useFirestore();
  const person = useCurrentPerson();
  const location = useLocation();
  const { firebaseUser } = useAuth();

  const [attributes, loadingAttributes] = useDocumentData(
    firestore.db.collection('attributes').doc(person.id)
  );

  const PSIDashboardIsLocked =
    firestore && !loadingAttributes && !attributes?.lastPsiId;

  const LockedWrapper: React.FC = PSIDashboardIsLocked
    ? (props) => (
        <Tooltip copy="Complete health questionnaire to unlock.">
          {props.children}
        </Tooltip>
      )
    : (props) => <>{props.children}</>;

  return (
    <>
      <LinksContainer>
        <NavBarLink activeClassName="active" exact to="/dashboard">
          <HomeIcon />
          Dashboard
        </NavBarLink>
        <LockedWrapper>
          <NavBarLink
            activeClassName="active"
            className="tooltip"
            disabled={PSIDashboardIsLocked}
            to="/dashboard/psi"
          >
            {PSIDashboardIsLocked ? <LockIcon /> : <HeartIcon />}
            Health
          </NavBarLink>
        </LockedWrapper>
        <NavBarLink activeClassName="active" to="/account">
          <AccountIcon />
          Account
        </NavBarLink>
      </LinksContainer>
      <SignOutLink>
        <ContainerSupportLink>
          <LinkButton
            onClick={() => {
              track(Events.ContactUsClicked, {
                sage_person_id: firebaseUser?.uid,
                url: location.pathname,
                button: 'support'
              });
              window.open(mailTo, '_blank');
            }}
          >
            Support
          </LinkButton>
        </ContainerSupportLink>
        <Link to="/logout">
          <SignOut variant="secondary">Sign Out</SignOut>
        </Link>
      </SignOutLink>
    </>
  );
};

const LinksContainer = styled.ul`
  display: flex;
  flex-direction: column;
  z-index: ${Zindex.NavLinks};
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: 100%;

  @media ${Device.laptop} {
    padding: 0 ${Spacing.sm};
    width: auto;
  }
`;

const NavBarLink = styled(NavLink)<{
  activeClassName: string;
  disabled?: boolean;
}>`
  padding: ${Spacing.sm};
  margin-bottom: 10px;
  font-family: 'EuclidCircularB', sans-serif;
  color: ${(props) => (props.disabled ? Colors.grey5 : Colors.sage3)};
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'all')};

  @media ${Device.laptop} {
    padding: ${Spacing.sm} ${Spacing.sm} ${Spacing.sm} ${Spacing.ml};
    flex-direction: row;
    justify-content: flex-start;
  }

  &.tooltip {
    position: relative;
  }
  svg {
    font-size: 22px;
    margin-bottom: ${Spacing.s};
    @media ${Device.laptop} {
      margin-right: ${Spacing.m};
      margin-bottom: 0;
    }
  }
`;

const SignOut = styled(PageButton)`
  width: 100%;
  max-width: 100%;
`;

const ContainerSupportLink = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${Spacing.s};
`;

const SignOutLink = styled.div`
  position: relative;
  margin-top: auto;

  @media ${Device.laptop} {
    position: sticky;
    bottom: ${Spacing.sm};
    left: 0;
    right: 0;
    margin: auto ${Spacing.sm} ${Spacing.sm};
    background-color: ${Colors.grey1};
    z-index: ${Zindex.NavControl};
    button {
      position: relative;
    }
    &:before {
      content: '';
      display: block;
      position: absolute;
      width: calc(100% + 2 * ${Spacing.sm});
      height: calc(100% + 2 * ${Spacing.sm});
      left: -${Spacing.sm};
      bottom: -${Spacing.sm};
    }
  }
`;
